import { NavigableItem } from './schemableFormNavigation';
import { ConfigItem } from './types';

const SEVERITY_RANK: Record<string, number> = { error: 0, warning: 1, ai_action: 2 };
const UNKNOWN_SEVERITY_RANK = 99;

export const sortArrayBasedOnConfig = <T extends NavigableItem>(
  items: Array<T>,
  configuration: Array<ConfigItem>,
): Array<T> => {
  const getConfigIndex = (segment: string): number => {
    return configuration.findIndex((config) => config.type === segment);
  };

  const getFieldWeight = (configItem: ConfigItem | undefined, path: Array<string>): number => {
    if (!configItem) {
      return Infinity;
    }
    const fields = configItem.fields || [];
    let currentFields = fields;
    let weight = 0;
    let depth = 0;

    for (let i = 0; i < path.length; i++) {
      const segment = path[i];
      if (!isNaN(Number(segment))) {
        continue;
      }

      const fieldIndex = currentFields.findIndex((field) => 'path' in field && field.path === segment);

      if (fieldIndex !== -1) {
        weight = weight * 1000 + fieldIndex;
        const field = currentFields[fieldIndex];
        if ('fields' in field && field.fields) {
          currentFields = field.fields;
          depth++;
        } else {
          break;
        }
      } else {
        const remainingPath = path
          .slice(i)
          .filter((s) => isNaN(Number(s)))
          .join('.');
        const dottedIndex = currentFields.findIndex((f) => 'path' in f && f.path === remainingPath);
        if (dottedIndex === -1) {
          return Infinity;
        }
        weight = weight * 1000 + dottedIndex;
        break;
      }
    }

    return weight * Math.pow(1000, 10 - depth); // Adjust weight based on depth
  };

  const comparePaths = (aPath: Array<string>, bPath: Array<string>): number => {
    const aConfigIndex = getConfigIndex(aPath[0]);
    const bConfigIndex = getConfigIndex(bPath[0]);

    // If configs have different indices, sort based on index (-1 means not found)
    if (aConfigIndex !== bConfigIndex) {
      // If one exists in config and other doesn't, prioritize the one in config
      if (aConfigIndex === -1) {
        return 1;
      } // b comes before a
      if (bConfigIndex === -1) {
        return -1;
      } // a comes before b

      // Both have config indices, compare them directly
      return aConfigIndex - bConfigIndex;
    }

    // If neither path was found in the configuration, compare lexicographically
    if (aConfigIndex === -1) {
      return aPath.join('.').localeCompare(bPath.join('.'));
    }

    const configItem = configuration[aConfigIndex];

    for (let i = 1; i < Math.min(aPath.length, bPath.length); i++) {
      if (aPath[i] !== bPath[i]) {
        // If both segments are numeric, compare them
        if (!isNaN(Number(aPath[i])) && !isNaN(Number(bPath[i]))) {
          return Number(aPath[i]) - Number(bPath[i]);
        }

        // If we've reached a non-numeric difference, compare weights
        const aWeight = getFieldWeight(configItem, aPath.slice(1));
        const bWeight = getFieldWeight(configItem, bPath.slice(1));
        if (aWeight !== bWeight) {
          return aWeight - bWeight;
        }
      }
    }

    return aPath.length - bPath.length;
  };

  const errorBearingFieldPaths = new Set<string>();
  for (const item of items) {
    if (item.severity === 'error') {
      errorBearingFieldPaths.add(item.fieldPath);
    }
  }

  return items
    .map((item, index) => ({ item, index }))
    .sort((a, b) => {
      const aErr = errorBearingFieldPaths.has(a.item.fieldPath) ? 0 : 1;
      const bErr = errorBearingFieldPaths.has(b.item.fieldPath) ? 0 : 1;
      if (aErr !== bErr) {
        return aErr - bErr;
      }
      const aPath = a.item.fieldPath.split('.');
      const bPath = b.item.fieldPath.split('.');
      const pathCmp = comparePaths(aPath, bPath);
      if (pathCmp !== 0) {
        return pathCmp;
      }
      const aSev = SEVERITY_RANK[a.item.severity ?? ''] ?? UNKNOWN_SEVERITY_RANK;
      const bSev = SEVERITY_RANK[b.item.severity ?? ''] ?? UNKNOWN_SEVERITY_RANK;
      if (aSev !== bSev) {
        return aSev - bSev;
      }
      return a.index - b.index;
    })
    .map((entry) => entry.item);
};
